import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ProfileService } from '../../core/services/profile.service';
import { UserStateService } from '../../core/services/user-state.service';
import { LoggingService } from '../../core/services/logging.service';
import { AuthHeaderComponent } from '../../shared/ui/auth-layout/auth-header.component';
import { AuthFooterComponent } from '../../shared/ui/auth-layout/auth-footer.component';

@Component({
  selector: 'app-complete-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, AuthHeaderComponent, AuthFooterComponent],
  templateUrl: './complete-profile.component.html',
  styleUrls: ['./complete-profile.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteProfileComponent implements OnInit {
  private logger = inject(LoggingService);
  profileForm: FormGroup;
  loading = signal(false);
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private userStateService: UserStateService,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      marketingConsent: [false] // Default to unchecked for GDPR compliance
    });
  }

  ngOnInit(): void {
    // If the user's profile is already complete, skip this step
    this.userStateService.getOrFetchState().subscribe({
      next: (status) => {
        if (status.profileComplete) {
          this.router.navigate(['/resolving']);
        }
      },
      error: (err) => {
        this.logger.error('Error checking onboarding status:', err);
      }
    });
  }

  onSubmit(): void {
    if (this.profileForm.invalid) {
      return;
    }

    this.loading.set(true);
    this.error = null;

    const profileData = {
      userId: '',
      firstName: this.profileForm.value.firstName.trim(),
      lastName: this.profileForm.value.lastName.trim(),
      marketingConsent: this.profileForm.value.marketingConsent || false
    };

    this.profileService.updateUserProfile(profileData).subscribe({
      next: (updatedProfile) => {
        this.logger.info('Profile updated successfully:', updatedProfile);
        // Update state so the guard and resolving page see the change immediately
        this.userStateService.updateState({ profileComplete: true });
        this.loading.set(false);
        // Redirect to resolving page to determine next step
        this.router.navigate(['/resolving']);
      },
      error: (err) => {
        this.logger.error('Error updating profile:', err);
        this.error = 'Failed to update profile. Please try again.';
        this.loading.set(false);
      }
    });
  }

  onSkip(): void {
    // User has chosen to skip for now. They will be prompted again on next login
    // when the resolving page detects profileComplete is still false.
    this.router.navigate(['/dashboard']);
  }

  get firstName() {
    return this.profileForm.get('firstName');
  }

  get lastName() {
    return this.profileForm.get('lastName');
  }
}
